import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';

import { formatFlashRatePlanName } from '../utils';

export const FLASH_RATE_PLAN_STATUS = 'content-approved';
export const FLASH_RATE_PLAN_PRODUCT_TYPE = ROOM_RATE_FLASH_TYPE;
export const FLASH_RATE_PLAN_RATE_TYPE = 'gross';

export type RatePlanCreateSchema = {
  properties?: Record<string, { enum?: Array<string> }>;
};

type RatePlanSelectField = 'cancellation_policy' | 'status' | 'product_type' | 'rate_type';

type RatePlanValidationParams = {
  ratePlanCancellationPolicy: string;
  ratePlanName: string;
  ratePlanRateType: string;
  ratePlanStatus: string;
};

export function getDefaultFlashRatePlanName(date = new Date()) {
  return formatFlashRatePlanName(date);
}

export type FlashRatePlanDraft = {
  createdRatePlanId?: string;
  draftId: string;
  ratePlanCancellationPolicy: string;
  ratePlanName: string;
  ratePlanRateType: string;
  ratePlanStatus: string;
};

type BuildDefaultRatePlanDraftParams = {
  draftId: string;
  ratePlanCancellationPolicy?: string;
  ratePlanName?: string;
  ratePlanRateType?: string;
  ratePlanStatus?: string;
};

export function buildDefaultRatePlanDraft({
  draftId,
  ratePlanCancellationPolicy = '',
  ratePlanName = getDefaultFlashRatePlanName(),
  ratePlanRateType = FLASH_RATE_PLAN_RATE_TYPE,
  ratePlanStatus = FLASH_RATE_PLAN_STATUS,
}: BuildDefaultRatePlanDraftParams): FlashRatePlanDraft {
  return {
    draftId,
    ratePlanCancellationPolicy,
    ratePlanName,
    ratePlanRateType,
    ratePlanStatus,
  };
}

export function getRatePlanDraftLabel(draft: FlashRatePlanDraft, index: number) {
  return draft.ratePlanName.trim() || `Rate plan ${index + 1}`;
}

export function isCreatedRatePlanDraft(draft: FlashRatePlanDraft) {
  return !!draft.createdRatePlanId;
}

export function buildCreatedRatePlansFromDrafts(
  existingPlans: Array<App.RatePlan>,
  newPlans: Array<App.RatePlan>,
  drafts: Array<FlashRatePlanDraft>,
): Array<App.RatePlan> {
  const plansById = new Map(existingPlans.map((ratePlan) => [ratePlan.id, ratePlan]));
  newPlans.forEach((ratePlan) => {
    plansById.set(ratePlan.id, ratePlan);
  });

  return drafts
    .map((draft) => (draft.createdRatePlanId ? plansById.get(draft.createdRatePlanId) : undefined))
    .filter((ratePlan): ratePlan is App.RatePlan => !!ratePlan);
}

export function getRatePlanDraftsValidationError(drafts: Array<FlashRatePlanDraft>) {
  for (let index = 0; index < drafts.length; index += 1) {
    if (isCreatedRatePlanDraft(drafts[index])) {
      continue;
    }

    const validationError = getRatePlanValidationError(drafts[index]);

    if (validationError) {
      return `Rate plan ${index + 1}: ${validationError}`;
    }
  }

  return null;
}

export function getRatePlanEnumOptions(
  schema: RatePlanCreateSchema | undefined,
  field: RatePlanSelectField,
  currentValue: string,
) {
  const schemaOptions = schema?.properties?.[field]?.enum?.filter(Boolean) ?? [];

  return currentValue && !schemaOptions.includes(currentValue) ? [currentValue, ...schemaOptions] : schemaOptions;
}

export function getRatePlanValidationError({
  ratePlanCancellationPolicy,
  ratePlanName,
  ratePlanRateType,
  ratePlanStatus,
}: RatePlanValidationParams) {
  if (!ratePlanName.trim()) {
    return 'Enter a rate plan name.';
  }

  if (!ratePlanCancellationPolicy.trim() || !ratePlanStatus.trim() || !ratePlanRateType.trim()) {
    return 'Complete all rate plan fields.';
  }

  return null;
}
